import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, CreditCard, RefreshCw, Sparkles, Eye, Users, Globe, TrendingUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import DashboardLayout, { StatusBadge, STATUS_STAGES } from "../../components/layout/DashboardLayout";
import api from "../../lib/api";

export default function ClientPortal() {
  const [projects, setProjects] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const loadAnalytics = () => api.get("/analytics/me").then((r) => setAnalytics(r.data)).catch(() => {});
  const load = () => {
    api.get("/projects/me").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/invoices/me").then((r) => setInvoices(r.data)).catch(() => {});
    loadAnalytics();
  };
  const viewed = useRef(false);
  useEffect(() => {
    load();
    if (!viewed.current) {
      viewed.current = true;
      api.post("/analytics/view").then(loadAnalytics).catch(() => {});
    }
  }, []);

  return (
    <DashboardLayout
      title="Client Portal"
      actions={<Button variant="outline" className="rounded-full" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>}
    >
      {/* Projects */}
      <section data-testid="client-projects" className="mb-10">
        <h2 className="font-display text-xl font-semibold text-[#F3EDDC] mb-4">Your Projects</h2>
        {projects === null ? (
          <div className="grid md:grid-cols-2 gap-5">{[0, 1].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : projects.length === 0 ? (
          <Card className="p-10 text-center border-dashed">
            <p className="text-[#948C79]">You don't have any projects yet.</p>
            <Button asChild className="mt-4 rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black"><Link to="/pricing">Choose a package</Link></Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {projects.map((p) => (
              <Card key={p.id} data-testid={`project-card-${p.id}`} className="p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#7E7663] capitalize">{p.package} · ${p.price}</div>
                    <h3 className="mt-1 font-display text-lg font-semibold text-[#F3EDDC]">{p.name}</h3>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="mt-4 text-sm text-[#948C79]">Revisions used: {p.revisions_used}/{p.revisions_allowed}</div>
                {(() => { const idx = STATUS_STAGES.indexOf(p.status); const pct = idx >= 0 ? Math.round(((idx + 1) / STATUS_STAGES.length) * 100) : 0; return (
                  <div className="mt-3" data-testid={`project-progress-${p.id}`}>
                    <div className="flex items-center justify-between text-xs text-[#7E7663] mb-1"><span>Progress</span><span className="text-[#C9A227] font-semibold">{pct}%</span></div>
                    <div className="h-1.5 w-full rounded-full bg-[#1b1b1d] overflow-hidden"><div className="h-full bg-[#C9A227] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                  </div>
                ); })()}
                <div className="mt-5 flex gap-2">
                  <Button asChild size="sm" className="rounded-full bg-black hover:bg-[#242424] text-white"><Link to={`/portal/project/${p.id}`} data-testid={`open-project-${p.id}`}>Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
                  {["Paid", "Onboarding"].includes(p.status) && (
                    <Button asChild size="sm" variant="outline" className="rounded-full"><Link to={`/portal/onboarding/${p.id}`}><Sparkles className="h-3.5 w-3.5 mr-1" /> Onboarding</Link></Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Analytics */}
      <section data-testid="client-analytics" className="mb-10">
        <h2 className="font-display text-xl font-semibold text-[#F3EDDC] mb-4">Your Website Analytics</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <AnalyticsStat icon={Eye} label="Total visits" value={analytics?.total_views} testid="analytics-total-visits" />
          <AnalyticsStat icon={TrendingUp} label="Visits (7 days)" value={analytics?.views_7} testid="analytics-views-7" />
          <AnalyticsStat icon={Users} label="Leads" value={analytics?.leads_count} testid="analytics-leads" />
          <AnalyticsStat icon={Globe} label="Live sites" value={analytics?.published} testid="analytics-live-sites" />
        </div>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-[#F3EDDC] text-sm">Visits — last 14 days</h3>
            <span className="text-xs text-[#7E7663]">Engagement tracked in your portal</span>
          </div>
          {!analytics ? (
            <Skeleton className="h-32 rounded-xl" />
          ) : (
            <div className="flex items-end gap-1.5 h-32">
              {analytics.daily.map((d, i) => {
                const max = Math.max(1, ...analytics.daily.map((x) => x.views));
                const h = Math.max(4, Math.round((d.views / max) * 112));
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                    <div data-testid={`analytics-bar-${i}`} className="w-full rounded-t bg-[#C9A227]/80 hover:bg-[#C9A227] transition-colors" style={{ height: `${h}px` }} title={`${d.date}: ${d.views}`} />
                    <span className="mt-1 text-[9px] text-[#7E7663]">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </section>

      {/* Invoices */}
      <section data-testid="client-invoices">
        <h2 className="font-display text-xl font-semibold text-[#F3EDDC] mb-4">Invoices</h2>
        <Card className="divide-y divide-white/10">
          {invoices.length === 0 ? (
            <div className="p-6 text-sm text-[#948C79] flex items-center gap-2"><CreditCard className="h-4 w-4" /> No invoices yet.</div>
          ) : invoices.map((inv) => (
            <div key={inv.session_id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-[#7E7663]" />
                <div>
                  <div className="text-sm font-medium text-[#F3EDDC]">{inv.package_name}</div>
                  <div className="text-xs text-[#7E7663]">{new Date(inv.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-[#F3EDDC]">${inv.amount}</div>
                <span className="text-xs text-emerald-600 font-medium">Paid</span>
              </div>
            </div>
          ))}
        </Card>
      </section>
    </DashboardLayout>
  );
}

function AnalyticsStat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#948C79]">{label}</span>
        <Icon className="h-4 w-4 text-[#C9A227]" />
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-[#F3EDDC]">{value ?? "—"}</div>
    </Card>
  );
}
